import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';

// Module-level cache to store fetched images by URL
// This prevents duplicate API calls when components re-render or same image appears in multiple places
// Exported so ShareSocialButton can reuse cached images during share
export const imageCache = new Map();

// Optional: Limit cache size to prevent memory issues (LRU-style cleanup)
const MAX_CACHE_SIZE = 40;
const cleanupCache = () => {
  if (imageCache.size > MAX_CACHE_SIZE) {
    // Remove oldest entries (first 20%)
    const keysToDelete = Array.from(imageCache.keys()).slice(0, Math.floor(MAX_CACHE_SIZE * 0.2));
    keysToDelete.forEach(key => {
      const url = imageCache.get(key);
      if (url) URL.revokeObjectURL(url);
      imageCache.delete(key);
    });
  }
};

/**
 * SecureImage Component
 * 
 * This component fetches images with proper JWT authentication headers
 * instead of using regular <img> tags which cannot send headers.
 * 
 * @param {Object} props
 * @param {string} props.src - The image URL to fetch
 * @param {string} props.alt - Alt text for the image
 * @param {Object} props.style - CSS styles to apply
 * @param {Function} props.onLoad - Callback when image loads
 * @param {Function} props.onError - Callback when image fails to load
 * @param {string} props.className - CSS class names
 * @param {Object} props.otherProps - Any other props to pass through
 */
const SecureImage = ({
  src,
  alt,
  style = {},
  onLoad,
  onError,
  className,
  inline = false,
  useThumbnail = false, // New prop for gallery optimization
  thumbnailSize = 'thumbnail', // Default to generic thumbnail (400x400 in backend)
  hideLoader = false, // New prop to hide loading spinner (useful for video posters)
  ...otherProps
}) => {
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const canvasRef = useRef(null);
  const location = useLocation();

  // Use refs to store callbacks to avoid re-fetching on every render
  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);

  // Detect if we're in admin portal context
  const isAdminPortal = location.pathname.startsWith('/glam-admin/');

  // Update refs when callbacks change
  useEffect(() => {
    onLoadRef.current = onLoad;
    onErrorRef.current = onError;
  }, [onLoad, onError]);

  useEffect(() => {
    if (!src) {
      setError(true);
      setLoading(false);
      return;
    }

    const fetchImage = async () => {
      try {
        // Helper to add or replace query parameters
        const updateQueryParam = (url, param, value) => {
          const urlObj = new URL(url.startsWith('http') ? url : `http://dummy.com${url}`);
          urlObj.searchParams.set(param, value);
          return url.startsWith('http') ? urlObj.toString() : urlObj.toString().replace('http://dummy.com', '');
        };

        // Build final URL with thumbnail parameter if needed
        let fetchUrl = src;
        if (useThumbnail && !src.includes('amazonaws.com') && !src.includes('s3.amazonaws.com') && !src.includes('size=')) {
          // Only add thumbnail param to backend API URLs if not already present
          fetchUrl = updateQueryParam(src, 'size', thumbnailSize);
        }

        // Check cache first to avoid duplicate fetch
        if (imageCache.has(fetchUrl)) {
          const cachedUrl = imageCache.get(fetchUrl);
          setImageData(cachedUrl);
          setLoading(false);
          setError(false);
          if (onLoadRef.current) {
            onLoadRef.current({ target: { src: cachedUrl } });
          }
          return;
        }

        // Clear previous image data and set loading state to avoid "flash" of old content
        setImageData(null);
        setLoading(true);
        setError(false);

        // PROGRESSIVE LOADING: 
        // 1. If this is NOT a thumbnail request, check if we already have the thumbnail cached
        // 2. OR if this IS a large thumbnail (1200x1200), check if we have the small one (400x400) cached
        const isLargeThumbnail = fetchUrl.includes('size=1200x1200');
        const isNotThumbnail = !useThumbnail && !src.includes('size=');

        if ((isNotThumbnail || isLargeThumbnail) && !src.includes('amazonaws.com') && !src.includes('s3.amazonaws.com')) {
          // Try multiple possible cache keys for 400x400 thumbnails
          // (both 'thumbnail' and '400x400' since they may be used interchangeably)
          const possibleKeys = [
            updateQueryParam(src, 'size', 'thumbnail'),   // size=thumbnail
            updateQueryParam(src, 'size', '400x400')       // size=400x400
          ];

          let cachedThumb = null;

          for (const key of possibleKeys) {
            if (imageCache.has(key)) {
              cachedThumb = imageCache.get(key);
              break;
            }
          }

          if (cachedThumb) {
            setImageData(cachedThumb);
            setLoading(false); // Stop showing the spinner, show the thumbnail instead

            // Trigger onLoad for the placeholder thumbnail so parent can hide its loading indicator
            if (onLoadRef.current) {
              onLoadRef.current({ target: { src: cachedThumb } });
            }
          }
        }

        // Check if this is an S3 URL (pre-signed URLs don't need authentication)
        if (fetchUrl.includes('amazonaws.com') || fetchUrl.includes('s3.amazonaws.com')) {
          // This is an S3 URL - load directly without authentication
          const response = await fetch(fetchUrl);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);

          // Store in cache for future use
          imageCache.set(fetchUrl, objectUrl);
          cleanupCache();

          setImageData(objectUrl);

          if (onLoadRef.current) {
            onLoadRef.current({ target: { src: objectUrl } });
          }
        } else {
          // This is a backend endpoint - use authentication based on context
          let token;

          if (isAdminPortal) {
            // In admin portal, use admin token to access any merchant's data
            // Check localStorage first, then cookies as fallback for backward compatibility
            token = localStorage.getItem('access_token') || Cookies.get('access_token');
            if (!token) {
              throw new Error('No admin authentication token found');
            }
          } else {
            // In merchant portal, use merchant token
            token = Cookies.get('merchant_access_token');
            if (!token) {
              throw new Error('No merchant authentication token found');
            }
          }

          // Fetch image with proper authentication headers
          const response = await fetch(fetchUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            // Enable redirect following
            redirect: 'follow'
          });

          // Check if response is ok or if it's a redirect that was followed
          if (!response.ok && response.status !== 302) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          // Convert response to blob
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);

          // Store in cache for future use
          imageCache.set(fetchUrl, objectUrl);
          cleanupCache();

          setImageData(objectUrl);

          // Call onLoad callback if provided
          if (onLoadRef.current) {
            onLoadRef.current({ target: { src: objectUrl } });
          }
        }

      } catch (err) {
        setError(true);

        // Call onError callback if provided
        if (onErrorRef.current) {
          onErrorRef.current({ target: { src: null } });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchImage();

    // Cleanup function - removed revocation to prevent breaking global imageCache
    return () => {
      // Do not revoke object URL here as it's stored in the global imageCache
    };
  }, [src, isAdminPortal, useThumbnail]); // Added useThumbnail to dependencies

  // Show loading state with placeholder
  if (loading) {
    // If hideLoader is true, render a transparent placeholder without spinner
    if (hideLoader) {
      return (
        <Box
          sx={{
            ...style,
            bgcolor: 'transparent',
          }}
          className={className}
        />
      );
    }

    if (inline) {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: style.width || '50px',
            height: style.height || '50px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            ...style
          }}
          className={className}
        >
          <CircularProgress size={16} />
        </span>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
          ...style
        }}
        className={className}
      >
        <CircularProgress size={24} />
        {/* <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          Loading...
        </Typography> */}
      </Box>
    );
  }

  // Show error state
  if (error) {
    if (inline) {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: style.width || '50px',
            height: style.height || '50px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            color: '#d32f2f',
            fontSize: '12px',
            ...style
          }}
          className={className}
        >
          No Image
        </span>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
          flexDirection: 'column',
          gap: 1,
          ...style
        }}
        className={className}
      >
        <Typography variant="body2" color="error">
          Failed to load image
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Please try again later
        </Typography>
      </Box>
    );
  }

  // Show image
  if (imageData) {
    return (
      <img
        src={imageData}
        alt={alt}
        style={{
          ...style,
          transition: 'opacity 0.3s ease-in-out',
          opacity: imageData ? 1 : 0
        }}
        className={className}
        {...otherProps}
      />
    );
  }

  // Fallback
  return null;
};

export default SecureImage;
